import { createBrowserAudioContext } from './context';
import { canPlaySfx } from './soundSettings';
import { nudgeAmbientResume } from './ambient';
import { nudgeMusicResume } from './music';

export { createBrowserAudioContext } from './context';

export type ActionCueId = 'collect' | 'build' | 'upgrade' | 'unlock' | 'remove';

export type FeedbackOscillator = {
  type: OscillatorType;
  frequency: {
    setValueAtTime(value: number, time: number): void;
  };
  connect(node: unknown): void;
  start(when?: number): void;
  stop(when?: number): void;
  disconnect(): void;
  onended: ((this: AudioScheduledSourceNode, ev: Event) => unknown) | (() => void) | null;
};

export type FeedbackGain = {
  gain: {
    setValueAtTime(value: number, time: number): void;
    linearRampToValueAtTime(value: number, time: number): void;
    exponentialRampToValueAtTime(value: number, time: number): void;
  };
  connect(node: unknown): void;
  disconnect(): void;
};

export type FeedbackAudioContext = {
  currentTime: number;
  state: string;
  destination: object;
  resume?: () => Promise<void> | void;
  createOscillator(): FeedbackOscillator;
  createGain(): FeedbackGain;
};

export type ActionCueTone = {
  freq: number;
  start: number;
  duration: number;
  gain: number;
  type: OscillatorType;
};

export type ActionCueSpec = {
  id: ActionCueId;
  totalMs: number;
  tones: readonly ActionCueTone[];
};

/** Soft attack so the first sample is not a click. Slightly longer than COL-006B's 16 ms. */
export const CUE_ATTACK_S = 0.018;

/**
 * COL-006B-C1 five-cue family: COL-006B softness kept, plus a success-only Remove cue.
 * Consonant C-major / nearby intervals; sine/triangle only; all totals <= 350 ms.
 * Not an imitation of any proprietary notification melody.
 */
export const ACTION_CUES: Record<ActionCueId, ActionCueSpec> = {
  collect: {
    id: 'collect',
    totalMs: 120,
    tones: [
      { freq: 523.25, start: 0, duration: 0.075, gain: 0.024, type: 'sine' },
      { freq: 659.25, start: 0.04, duration: 0.08, gain: 0.018, type: 'sine' },
    ],
  },
  build: {
    id: 'build',
    totalMs: 210,
    tones: [
      { freq: 246.94, start: 0, duration: 0.16, gain: 0.034, type: 'triangle' },
      { freq: 329.63, start: 0.08, duration: 0.13, gain: 0.026, type: 'sine' },
    ],
  },
  upgrade: {
    id: 'upgrade',
    totalMs: 260,
    tones: [
      { freq: 329.63, start: 0, duration: 0.09, gain: 0.032, type: 'sine' },
      { freq: 392, start: 0.08, duration: 0.09, gain: 0.03, type: 'sine' },
      { freq: 523.25, start: 0.16, duration: 0.1, gain: 0.026, type: 'sine' },
    ],
  },
  unlock: {
    id: 'unlock',
    totalMs: 330,
    tones: [
      { freq: 196, start: 0, duration: 0.14, gain: 0.032, type: 'triangle' },
      { freq: 261.63, start: 0.06, duration: 0.14, gain: 0.03, type: 'sine' },
      { freq: 329.63, start: 0.13, duration: 0.14, gain: 0.028, type: 'sine' },
      { freq: 392, start: 0.2, duration: 0.13, gain: 0.024, type: 'sine' },
    ],
  },
  remove: {
    id: 'remove',
    totalMs: 200,
    tones: [
      { freq: 392, start: 0, duration: 0.09, gain: 0.026, type: 'sine' },
      { freq: 329.63, start: 0.06, duration: 0.1, gain: 0.024, type: 'sine' },
      { freq: 261.63, start: 0.12, duration: 0.08, gain: 0.022, type: 'sine' },
    ],
  },
};

export const ACTION_CUE_MAX_MS = 350;

export class NativeActionFeedback {
  private context: FeedbackAudioContext | null = null;
  private resolved = false;

  constructor(
    private readonly createContext: () => FeedbackAudioContext | null = createBrowserAudioContext,
  ) {}

  play(cue: ActionCueId): void {
    const ctx = this.ensureContext();
    if (!ctx) {
      return;
    }
    try {
      const spec = ACTION_CUES[cue];
      if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
        void Promise.resolve(ctx.resume())
          .then(() => {
            try {
              scheduleCue(ctx, spec);
            } catch {
              // Unsupported or interrupted Web Audio must stay a silent no-op.
            }
          })
          .catch(() => undefined);
        return;
      }
      scheduleCue(ctx, spec);
    } catch {
      // Unsupported or interrupted Web Audio must stay a silent no-op.
    }
  }

  private ensureContext(): FeedbackAudioContext | null {
    if (this.resolved) {
      return this.context;
    }
    this.resolved = true;
    try {
      this.context = this.createContext();
    } catch {
      this.context = null;
    }
    return this.context;
  }
}

const nativeActionFeedback = new NativeActionFeedback();

export function playActionCue(cue: ActionCueId): void {
  if (!canPlaySfx()) {
    return;
  }
  nativeActionFeedback.play(cue);
  nudgeAmbientResume();
  nudgeMusicResume();
}

export function playSuccessfulActionCue(
  ok: boolean,
  cue: ActionCueId,
  player: (next: ActionCueId) => void = playActionCue,
): void {
  if (!ok) {
    return;
  }
  player(cue);
}

function scheduleCue(ctx: FeedbackAudioContext, spec: ActionCueSpec): void {
  for (const tone of spec.tones) {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    const startAt = ctx.currentTime + tone.start;
    oscillator.type = tone.type;
    oscillator.frequency.setValueAtTime(tone.freq, startAt);
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.linearRampToValueAtTime(tone.gain, startAt + CUE_ATTACK_S);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + tone.duration);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + tone.duration + 0.02);
    oscillator.onended = () => {
      oscillator.disconnect();
      gain.disconnect();
    };
  }
}
